import { defineComponent as _defineComponent } from 'vue'
import { Price, Subscription } from '@/@types/billing';
import { PaymentMethodDetails } from '@/@types/billing/subscription/PaymentMethodDetails';
import { ScheduledChangeActionEnum } from '@/@types/billing/subscription/ScheduledChange';
import PlanBenefits from '@/components/billing/PlanBenefits.vue';
import ChangePaymentMethod from '@/components/pages/settings/BillingPlanDetailsPage/ChangePaymentMethod.vue';
import CurrentPlan from '@/components/pages/settings/BillingPlanDetailsPage/CurrentPlan.vue';
import PlanUsage from '@/components/pages/settings/BillingPlanDetailsPage/PlanUsage.vue';
import { Company } from '@/models/Company';
import logger from '@/other/Logger';
import router from '@/router';
import { BILLING_PLANS, HOME } from '@/routerNames';
import apiService from '@/services/ApiService';
import confirmationService from '@/services/ConfirmationService';
import store from '@/store';
import { Loading } from 'element-ui';
import moment from 'moment/moment';
import { computed, onMounted, ref } from 'vue';
import { GUIDES_APP, usesBillingPages } from '@/helpers/appContext';
import { findCompanySubscription, findGuidesSubscription } from '@/helpers/planProduct';


export default /*#__PURE__*/_defineComponent({
  __name: 'BillingPlanDetailsPage',
  setup(__props) {

const prices = ref<Price[]>([]);
const subscription = ref<Subscription>();
const guidesSubscription = ref<Subscription>();
const paymentMethod = ref<PaymentMethodDetails>();

const company = computed((): Company => store.state.company);

const hasEnded = (target?: Subscription) =>
  ['paused', 'canceled'].includes(target?.status) ||
  target?.scheduledChange?.action === ScheduledChangeActionEnum.Cancel ||
  target?.scheduledChange?.action === ScheduledChangeActionEnum.Pause;

const isCanceledOrPaused = computed(() => hasEnded(subscription.value));
const hasGuidesEnded = computed(() => hasEnded(guidesSubscription.value));

const isScheduledChangeImminent = computed(() => {
  if (!nextBilledAt.value) {
    return false;
  }

  const now = moment.utc().local();
  const minutesUntilChange = nextBilledAt.value.diff(now, 'minutes');

  // If scheduled change is within 35 minutes, prevent plan changes
  return minutesUntilChange <= 35 && minutesUntilChange > 0;
});

const nextBilledAt = computed(() =>
  subscription.value?.currentBillingPeriod?.endsAt
    ? moment.utc(subscription.value.currentBillingPeriod.endsAt).local()
    : null
);
const lastBilledAt = computed(() =>
  subscription.value?.currentBillingPeriod?.startsAt
    ? moment.utc(subscription.value.currentBillingPeriod.startsAt).local()
    : null
);
const label = computed(() => {
  if (!nextBilledAt.value) {
    return isCanceledOrPaused.value ? 'Subscription ends on N/A' : 'Next billing date N/A';
  }
  return `${isCanceledOrPaused.value ? 'Subscription ends on' : 'Next billing date'} ${nextBilledAt.value.format(
    'MMM DD, YYYY'
  )}`;
});
const rangeMax = computed(() => {
  if (nextBilledAt.value && lastBilledAt.value) {
    return nextBilledAt.value.diff(lastBilledAt.value, 'days').toString();
  }
  return '0';
});
const rangeValue = computed(() => {
  if (lastBilledAt.value) {
    return moment().diff(lastBilledAt.value, 'days').toString();
  }
  return '0';
});

const changePlan = async () => {
  // C7: thread the ACTUAL current-subscription product through explicitly —
  // a fresh tab has empty sessionStorage, so PlansPage can no longer rely on
  // the session/query guides context alone to pick the right product's
  // prices for a change-plan visit.
  const query = company.value?.paddle?.app === GUIDES_APP ? { app: GUIDES_APP } : {};
  await router.push({ name: BILLING_PLANS, params: { update: 'update' }, query });
};

const pause = async (target: typeof subscription) => {
  await confirmationService.showCancelSubscriptionPopup();

  const loader = Loading.service({ lock: true });

  try {
    const { data: newSubscription } = await apiService.billing.pauseSubscription({
      subscriptionId: target.value?.id,
    });
    target.value = newSubscription;
  } catch (e) {
    apiService.handleResponseError(e);
  }

  loader.close();
  loader.$destroy();
};

const pauseSubscription = () => pause(subscription);
const pauseGuidesSubscription = () => pause(guidesSubscription);

const loadSubscription = async () => {
  await Promise.all([
    apiService.billing.getPrices().then(({ data }) => {
      prices.value = data;
    }),
    apiService.billing.getUserSubscriptions().then(({ data }) => {
      subscription.value = findCompanySubscription(data, company.value);
      guidesSubscription.value = findGuidesSubscription(data, company.value);
    }),
  ]);
};

const loadPaymentMethod = async () => {
  try {
    const { data } = await apiService.billing.getPaymentMethod(subscription.value?.id);
    if (data) {
      paymentMethod.value = data;
    }
  } catch (err) {
    logger.error(err);
  }
};

onMounted(async () => {
  if (!usesBillingPages(company.value)) {
    await router.push({ name: HOME });
    return;
  }

  const loader = Loading.service({ lock: true });

  try {
    await loadSubscription();
    await loadPaymentMethod();
  } catch (e) {
    apiService.handleResponseError(e);
  }

  loader.close();
  loader.$destroy();
});

return { __sfc: true,prices, subscription, guidesSubscription, paymentMethod, company, hasEnded, isCanceledOrPaused, hasGuidesEnded, isScheduledChangeImminent, nextBilledAt, lastBilledAt, label, rangeMax, rangeValue, changePlan, pause, pauseSubscription, pauseGuidesSubscription, loadSubscription, loadPaymentMethod, PlanBenefits, ChangePaymentMethod, CurrentPlan, PlanUsage }
}

})